import { useQuery } from "react-query";
import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import "./results.scss";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { ApiEndpoint, getScoringEquivalents } from "../components/utils";
import { AppDataContext, ctxDefinition } from "../components/ctx";
import AppHeader from "../components/appheader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { BrandColors } from "../components/utils";
import {
  Alert,
  Autocomplete,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  formatResultsData,
  questionCatDescriptions,
  pdfFooter,
  pdfHeader,
} from "../components/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import NetworkError from "../components/networkError";
import { BrandButton } from "../components/form/buttons";
import { renderToString } from "react-dom/server";
import { ExamResultsToHTML } from "../components/data2html";
import bgImage from "../assets/report_header_bg.png";
import leftOval from "../assets/left_oval.png";
import rightOval from "../assets/right_oval.png";
import answersBG from "../assets/answers_bg.png";
import VisibilitySensor from "react-visibility-sensor";

interface UserStats {
  worstCategories: any[] | null;
  topCategories: any[] | null;
  headline?: string;
  sat_summary?: any;
  act_summary?: any;
  act_answers?: any;
  sat_answers?: any;
  summary_tables?: any;
  summary_tables_1?: any;
  summary_tables_2?: any;
  decision?: string;
  satEquivalent?: number | string;
  actEquivalent?: number | string;
  userName: string;
  testDate: string;
  legacyACTScoring?: boolean;
}

interface AnswerRow {
  answer: string;
  correct: string;
  type: string;
}

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}

interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}

interface SearchResult {
  email: string;
  id: number;
  name: string;
}

const TooltipText = (text: string) => {
  return <p>{text}</p>;
};

const ExamResults = () => {
  const { userData } = useContext(AppDataContext) as ctxDefinition;
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [selectedUser, setSelectedUser] = useState<PlaceType | null | any>(
    null,
  );
  const [searchInput, setSearchInput] = useState<string>("");
  const [data, setdata] = useState<UserStats | null>();
  const [targetDataUser, setTargetDataUser] = useState<number>(userData.userid);
  const [hasError, setHasError] = useState(false);
  const [gettingReport, setGettingReport] = useState(false);
  const [reportError, setReportError] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const vertical = "bottom";
  const horizontal = "center";
  const testDate = useRef<string | null | any>(null);
  const [selectedUsername, setSelectedUsername] = useState<
    string | null | any
  >();
  const [defaultUser, setDefaultUser] = useState<string | null>(
    `${userData.firstName} ${userData.lastName}`,
  );

  dayjs.extend(utc);

  const { isLoading, error, refetch } = useQuery(
    "examResultsPageData",
    async () => {
      setLoadingData(true);
      const resp = await axios.post(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/testResults",
        {
          token: userData.token,
          userID: targetDataUser,
        },
      );
      setLoadingData(false);
      if (!resp || !resp.data.success) {
        setHasError(true);
        return;
      }

      const stats: any = formatResultsData(resp.data);
      testDate.current = dayjs
        .utc(resp.data.results[0].snapshotDatetime)
        .format("MM/DD/YYYY h:mma");

      //split summary tables into 2 columns....
      stats.summary_tables_1 = [
        stats.summary_tables[0],
        stats.summary_tables[2],
        stats.summary_tables[4],
        stats.summary_tables[6],
      ];
      stats.summary_tables_2 = [
        stats.summary_tables[1],
        stats.summary_tables[3],
      ];

      //detemine equality score...
      const tempScores = getScoringEquivalents(
        stats.act_summary.act_score,
        stats.sat_summary.sat_score,
      );
      stats.satEquivalent = tempScores[0];
      stats.actEquivalent = tempScores[1];
      stats.legacyACTScoring = resp.data.results?.legacyACTScoring;

      setdata(stats);
      checkDiagnosticResults(stats, targetDataUser);
    },
  );

  const checkDiagnosticResults = async (stats: any, userID: number) => {
    if (!userData.userid) return;

    await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/checkDianosticRecord",
      {
        token: userData.token,
        userid: userID,
        test_date: testDate.current.split(" ")[0],
        preferred_test: stats?.decision,
        sat_score: stats?.sat_summary?.sat_score,
        act_score: stats?.act_summary?.act_score,
      },
    );
  };

  const searchUsers = async () => {
    setSelectedUsername(null);
    setDefaultUser(null);
    const resp = await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/searchEmail",
      {
        token: userData.token,
        s: searchInput,
      },
    );
    if (resp.data.length > 0) {
      setSelectedUsername(
        resp.data.length > 1 ? "waiting... " : resp.data[0].name,
      );
      const ops = resp?.data.map((item: SearchResult) => {
        return { label: item.email, id: item.id };
      });
      setOptions(ops);
    }
  };

  const getTopAndWorstCategories = (
    summaryTables1: any,
    summaryTables2: any,
  ) => {
    // Group categories by title and aggregate their totals
    const categoryTotals: any = {};

    // Combine both summary table arrays
    const allTables = [...(summaryTables1 || []), ...(summaryTables2 || [])];

    allTables.forEach((table: any) => {
      // Skip ACT Science table and null tables
      if (!table || table.table_name === "ACT Science") {
        return;
      }

      Object.keys(table.categories).forEach((categoryKey) => {
        const category = table.categories[categoryKey];
        const categoryTitle = category.title;

        // Skip categories that don't have a title
        if (!categoryTitle || categoryTitle.trim() === "") {
          return;
        }

        if (!categoryTotals[categoryTitle]) {
          categoryTotals[categoryTitle] = {
            title: categoryTitle,
            correct: 0,
            incorrect: 0,
            len: 0,
            tables: [], // Track which tables this category appears in
          };
        }

        // Aggregate the totals
        categoryTotals[categoryTitle].correct += category.correct;
        categoryTotals[categoryTitle].incorrect += category.incorrect;
        categoryTotals[categoryTitle].len += category.len;
        categoryTotals[categoryTitle].tables.push(table.table_name);
      });
    });

    // Calculate percentage values and convert to array
    const aggregatedCategories = Object.keys(categoryTotals).map(
      (categoryTitle) => {
        const category = categoryTotals[categoryTitle];
        const value =
          category.len > 0
            ? Math.round((category.correct / category.len) * 100)
            : 0;

        return {
          ...category,
          value: parseFloat(value.toFixed(2)), // Round to 2 decimal places
        };
      },
    );

    // Sort by value (percentage score) - highest to lowest
    const sortedCategories = aggregatedCategories.sort(
      (a: any, b: any) => b.value - a.value,
    );

    // Get top 4 best scoring categories
    const topCategories = sortedCategories.slice(0, 4);

    // Get bottom 4 worst scoring categories
    const worstCategories = sortedCategories.slice(-4).reverse(); // reverse to show worst first

    return {
      topCategories,
      worstCategories,
      allCategories: sortedCategories, // Optional: return all sorted if needed
    };
  };

  const generatePDF = async () => {
    if (!data) return;
    setGettingReport(true);
    // add time and user information to the data object
    data.userName =
      selectedUser && selectedUsername && !defaultUser
        ? selectedUsername
        : defaultUser;
    data.testDate = testDate.current;

    //add greatest and least performing categories
    if (data.summary_tables_1 && data.summary_tables_2) {
      const result = getTopAndWorstCategories(
        data.summary_tables_1,
        data.summary_tables_2,
      );

      data.topCategories = result.topCategories;
      data.worstCategories = result.worstCategories;
    } else {
      data.topCategories = null;
      data.worstCategories = null;
    }

    const html = renderToString(ExamResultsToHTML(data));
    ////////////////////
    let email = pdfHeader + html + pdfFooter;

    // console.log("112233");
    // console.log(email); //........................................................
    // setGettingReport(false);
    // return;

    const resp = await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/getPDFreport",
      {
        token: userData.token,
        sandbox: false,
        source: email,
      },
    );
    if (resp.data.success) {
      if (resp.data.results.url) {
        window.open(resp.data.results.url, "_blank");
      } else {
        setReportError(true);
      }
    } else {
      setReportError(true);
    }
    setGettingReport(false);
  };

  const animateCountUp = (el: any) => {
    const countTo = parseInt(el.getAttribute("data-count"));
    let currentCount = 0;
    const duration = 2000; // duration in ms
    const steps = 60; // number of animation steps (60fps-like)
    const incrementTime = duration / steps; // time between each step
    const increment = countTo / steps; // how much to increase each step
    /////
    const counter = setInterval(() => {
      currentCount += increment;
      const displayCount = Math.round(currentCount);
      el.innerText = displayCount.toString();

      if (currentCount >= countTo) {
        el.innerText = countTo.toString(); // ensure final value is exact
        clearInterval(counter);
      }
    }, incrementTime);
  };

  useEffect(() => {
    if (selectedUser) {
      setTargetDataUser(selectedUser?.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDataUser]);

  useEffect(() => {
    if (searchInput && searchInput.length > 3) {
      searchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    const counterElements = document.querySelectorAll("#countup");
    counterElements.forEach((el) => {
      animateCountUp(el);
    });
  }, [data]);

  if (isLoading || !data?.summary_tables_1) return <Loader />;
  if (error) return <LoadError />;

  return (
    <div className="resultsPage">
      <div className="pageHeader">
        <AppHeader timer={false} />
        {userData.isadmin && (
          <>
            <div className="userSwitcher">
              <Autocomplete
                disablePortal
                options={options}
                value={selectedUser}
                sx={{ width: 300 }}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField {...params} label="choose user" />
                )}
                onChange={(event: any, newValue: any) => {
                  setOptions(newValue ? [newValue, ...options] : options);
                  setSelectedUser(newValue);
                }}
                onInputChange={(event: any, newInputValue: any) => {
                  setSearchInput(newInputValue);
                }}
                noOptionsText="No Users"
              />
            </div>
            {selectedUser && !loadingData && !isLoading && (
              <div className="pdfButtonHolder">
                <BrandButton disabled={gettingReport} onClick={generatePDF}>
                  {gettingReport ? "generating..." : "PDF Report"}
                </BrandButton>
              </div>
            )}
            {loadingData && (
              <div className="pdfButtonHolder">
                <BrandButton disabled>Loading...</BrandButton>
              </div>
            )}
          </>
        )}
        {!userData.isadmin && (
          <>
            {!loadingData && !isLoading && (
              <div className="pdfButtonHolder">
                <BrandButton disabled={gettingReport} onClick={generatePDF}>
                  {gettingReport ? "generating..." : "PDF Report"}
                </BrandButton>
              </div>
            )}
            {loadingData && (
              <div className="pdfButtonHolder">
                <BrandButton disabled>Loading...</BrandButton>
              </div>
            )}
          </>
        )}
      </div>

      <div className="pageContent results">
        <div
          className="reports_header"
          style={{
            backgroundImage: `url(${leftOval}), url(${rightOval}), url(${bgImage})`,
          }}
        >
          <div className="headline">
            <h1>Your Results</h1>
            <h3>
              <span>Name:</span>{" "}
              {selectedUser && selectedUsername && !defaultUser
                ? selectedUsername
                : defaultUser}
              <br /> <span>Test Date:</span>{" "}
              {testDate.current ? testDate.current : "unavailable"}
            </h3>
          </div>
          <h2>
            {data?.decision?.toString() === "both"
              ? `You performed equally well on the SAT and ACT portions of this diagnostic.`
              : `You're Better-Suited to ${data?.decision}!`}
          </h2>

          <div className="resultsHolder">
            <div className="resultsBox">
              <div
                className={[
                  "headline",
                  data?.decision === "the ACT" ? "active" : "",
                ].join(" ")}
              >
                {data?.decision === "the ACT" && (
                  <FontAwesomeIcon
                    color={BrandColors.brandDarkGreen}
                    icon={faCheck}
                  />
                )}
                ACT Composite:{" "}
                <span id="countup" data-count={data?.act_summary?.act_score}>
                  {data?.act_summary?.act_score}
                </span>
                <p className="equivalentHeader">
                  SAT Equivalent:{" "}
                  <span id="countup" data-count={data.satEquivalent}>
                    {data.satEquivalent}
                  </span>
                </p>
                {/* <span>({data?.act_summary?.sat_score} SAT)</span> */}
              </div>
              <div className="subheadline">
                {data?.legacyACTScoring
                  ? `Your ACT Composite score is the average of your scores on each of the three sections of the ACT: English, Math, Reading and Science.`
                  : `Your ACT composite score is the average of your scores on the English, Math, and Reading sections. Your Science score is reported separately and is not included in your composite score.`}
              </div>
              <div className="resultsTable">
                <div className="resultsRow">
                  <p>English:</p>
                  <p>{data?.act_summary?.details.english}</p>
                </div>
                <div className="resultsRow">
                  <p>Math:</p>
                  <p>{data?.act_summary?.details.math}</p>
                </div>
                <div className="resultsRow">
                  <p>Reading:</p>
                  <p>{data?.act_summary?.details.reading}</p>
                </div>
                {data?.legacyACTScoring && (
                  <div className="resultsRow">
                    <p>Science:</p>
                    <p>{data?.act_summary?.details.science}</p>
                  </div>
                )}
              </div>
              {!data?.legacyACTScoring && (
                <div className="resultsTable bottomTable">
                  <div className="resultsRow">
                    <p>Science:</p>
                    <p>{data?.act_summary?.details.science}</p>
                  </div>
                  <div className="resultsRow textOnly">
                    <p>
                      The Science score is not included in the Composite score.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="resultsBox">
              <div
                className={[
                  "headline",
                  data?.decision === "the SAT" ? "active" : "",
                ].join(" ")}
              >
                {data?.decision === "the SAT" && (
                  <FontAwesomeIcon
                    color={BrandColors.brandDarkGreen}
                    icon={faCheck}
                  />
                )}
                SAT Total Score:{" "}
                <span id="countup" data-count={data?.sat_summary?.sat_score}>
                  {data?.sat_summary?.sat_score}
                </span>
                <p className="equivalentHeader">
                  ACT Equivalent:{" "}
                  <span id="countup" data-count={data.actEquivalent}>
                    {data.actEquivalent}
                  </span>
                </p>
                {/* <span>({data?.sat_summary?.act_score} ACT)</span> */}
              </div>
              <div className="subheadline">
                Your SAT Total Score is the sum of your scores on the Reading
                and Writing and the Math portions of the SAT.
              </div>
              <div className="resultsTable active">
                <div className="resultsRow">
                  <p>Reading and Writing:</p>
                  <p>{data?.sat_summary?.details.verbal}</p>
                </div>
                <div className="resultsRow">
                  <p>Math:</p>
                  <p>{data?.sat_summary?.details.math}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="answersMatrix">
          <div className="answerMatrixContent">
            <div className="headline">
              ACT <span>Answers</span>
            </div>
            <div className="subheadline">
              Below is a detailed accounting of your performance on each of the
              four sections of the ACT portion of our diagnostic test. In each
              section, you’ll see the number of each question, the correct
              answer, your answer, and the Question Category it belongs to.
            </div>
            <div className="matrixTable">
              <div className="matrixColumn">
                <div className="matrixHeadline">
                  <p>
                    English: <span>{data?.act_summary?.details.english}</span>
                  </p>
                </div>
                <div className="matrixData">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.act_answers?.english.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div className="matrixData">
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>

              <div className="matrixColumn">
                <div className="matrixHeadline">
                  <p>
                    Math: <span>{data?.act_summary?.details.math}</span>
                  </p>
                </div>
                <div className="matrixData">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.act_answers?.math.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div key={index} className="matrixData">
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>

              <div className="matrixColumn">
                <div className="matrixHeadline">
                  <p>
                    Reading: <span>{data?.act_summary?.details.reading}</span>
                  </p>
                </div>
                <div className="matrixData">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.act_answers?.reading.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div className="matrixData" key={Math.random()}>
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>

              <div className="matrixColumn">
                <div className="matrixHeadline">
                  <p>
                    Science: <span>{data?.act_summary?.details.science}</span>
                  </p>
                </div>
                <div className="matrixData">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.act_answers?.science.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div key={index} className="matrixData">
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="answersMatrix"
          style={{
            backgroundImage: `url(${answersBG})`,
          }}
        >
          <div className="answerMatrixContent">
            <div className="headline">
              SAT <span>Answers</span>
            </div>
            <div className="subheadline">
              Below is a detailed accounting of your performance on each of the
              sections of the SAT portion of our diagnostic test. In each
              section, you’ll see the number of each question, the correct
              answer, your answer, and the Question Category it belongs to.
            </div>

            <div className="matrixTable two-col">
              <div className="matrixColumn sat">
                <div className="matrixHeadline sat">
                  <p>
                    Reading and Writing:{" "}
                    <span>{data?.sat_summary?.details.verbal}</span>
                  </p>
                </div>
                <div className="matrixHeadline subheadline">Module 1</div>
                <div className="matrixData first-row">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.sat_answers?.verbal.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div key={index} className="matrixData">
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
                <div className="matrixHeadline subheadline">Module 2</div>
                <div className="matrixData first-row">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.sat_answers?.verbal2.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div key={index} className="matrixData">
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>

              <div className="matrixColumn sat">
                <div className="matrixHeadline">
                  <p>
                    Math: <span>{data?.sat_summary?.details.math}</span>
                  </p>
                </div>
                <div className="matrixHeadline subheadline">Module 1</div>
                <div className="matrixData first-row">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.sat_answers?.math.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div className="matrixData" key={Math.random()}>
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
                <div className="matrixHeadline subheadline">Module 2</div>
                <div className="matrixData first-row">
                  <div className="matrixDataCol">
                    <p>Question #</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Your Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Correct Answer</p>
                  </div>
                  <div className="matrixDataCol">
                    <p>Question Category</p>
                  </div>
                </div>
                {data?.sat_answers?.math2.map(
                  (item: AnswerRow, index: number) => {
                    return (
                      <div className="matrixData" key={Math.random()}>
                        <div className="matrixDataCol">
                          <p>{index + 1}</p>
                        </div>
                        {item.answer ? (
                          <div className="matrixDataCol">
                            <p>{item.answer}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol unanswered">
                            <p>{item.answer}</p>
                          </div>
                        )}
                        {item.correct !== item.answer ? (
                          <div className="matrixDataCol incorrect">
                            <p>{item.correct}</p>
                          </div>
                        ) : (
                          <div className="matrixDataCol">
                            <FontAwesomeIcon
                              color={BrandColors.brandDarkGreen}
                              icon={faCheck}
                            />
                          </div>
                        )}

                        <div className="matrixDataCol">
                          <p>{item.type}</p>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="summary-table-headline">
          <h4>Performance Summary</h4>
          <p>
            Below is a detailed accounting of how well you performed on each of
            the Question Categories on our diagnostic test. This will help you
            determine the specific skills and question types you’ll need to
            improve upon to get to mastery.
          </p>
        </div>
        <div className="summary-table-holder">
          <div className="column">
            {(data?.summary_tables_1).map((item: any, index: number) => {
              if (!item)
                return <div className="summaryTable" key={Math.random()}></div>;
              return (
                <div className="summaryTable" key={Math.random()}>
                  <div className="headline">{item.table_name}</div>
                  <div className="tableHolder">
                    {Object.keys(item?.categories).map((row: any) => {
                      return (
                        <div className="tableRow">
                          <div className="tableCol donut">
                            <VisibilitySensor>
                              {({ isVisible }: { isVisible: boolean }) => {
                                const percentage = isVisible
                                  ? item.categories[row].value
                                  : 0;
                                return (
                                  <CircularProgressbar
                                    value={percentage}
                                    maxValue={100}
                                    text={`${Math.round(percentage)}%`}
                                    styles={buildStyles({
                                      textSize: "16px",
                                      textColor: "#fff",
                                      backgroundColor: "#cc9900",
                                      trailColor: "#686868",
                                      pathColor: "#bad7a7",
                                    })}
                                    strokeWidth={12}
                                  />
                                );
                              }}
                            </VisibilitySensor>
                          </div>
                          <div className="tableCol">
                            <p className="catName">
                              <Tooltip
                                className="tooltip"
                                title={TooltipText(
                                  questionCatDescriptions[
                                    item.categories[row]
                                      .title as keyof typeof questionCatDescriptions
                                  ],
                                )}
                                placement="top"
                              >
                                <p> {item.categories[row].title}</p>
                              </Tooltip>
                            </p>
                          </div>
                          <div className="tableCol">
                            <p>
                              # of Correct:{" "}
                              <span>{item.categories[row].correct}</span>
                            </p>
                          </div>
                          <div className="tableCol">
                            <p>
                              # of Incorrect:{" "}
                              <span>{item.categories[row].incorrect}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="column">
            {(data?.summary_tables_2).map((item: any, index: number) => {
              if (!item)
                return <div className="summaryTable" key={Math.random()}></div>;
              return (
                <div className="summaryTable" key={Math.random()}>
                  <div className="headline">{item.table_name}</div>
                  <div className="tableHolder">
                    {Object.keys(item?.categories).map((row: any) => {
                      return (
                        <div className="tableRow">
                          <div className="tableCol donut">
                            <VisibilitySensor>
                              {({ isVisible }: { isVisible: boolean }) => {
                                const percentage = isVisible
                                  ? item.categories[row].value
                                  : 0;
                                return (
                                  <CircularProgressbar
                                    value={percentage}
                                    maxValue={100}
                                    text={`${Math.round(percentage)}%`}
                                    styles={buildStyles({
                                      textSize: "16px",
                                      textColor: "#fff",
                                      backgroundColor: "#cc9900",
                                      trailColor: "#686868",
                                      pathColor: "#bad7a7",
                                    })}
                                    strokeWidth={12}
                                  />
                                );
                              }}
                            </VisibilitySensor>
                          </div>
                          <div className="tableCol">
                            <p className="catName">
                              <Tooltip
                                className="tooltip"
                                title={TooltipText(
                                  questionCatDescriptions[
                                    item.categories[row]
                                      .title as keyof typeof questionCatDescriptions
                                  ],
                                )}
                                placement="top"
                              >
                                <p> {item.categories[row].title}</p>
                              </Tooltip>
                            </p>
                          </div>
                          <div className="tableCol">
                            <p>
                              # of Correct:{" "}
                              <span>{item.categories[row].correct}</span>
                            </p>
                          </div>
                          <div className="tableCol">
                            <p>
                              # of Incorrect:{" "}
                              <span>{item.categories[row].incorrect}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <NetworkError open={hasError} />
      <Snackbar
        open={reportError}
        autoHideDuration={6000}
        onClose={() => setReportError(false)}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">
          There was an error generating your PDF report.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ExamResults;
